import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { useI18n } from "@/i18n";
import AdminNav from "@/components/AdminNav";
import MediaImage from "@/components/MediaImage";

const mono = "font-mono2 text-[10px] uppercase tracking-wider";
const inputCls = "rounded-md border border-[#1F293D] bg-[#0B0E14] px-3 py-2 text-sm text-slate-200 focus:border-[#00FF87] focus:outline-none";
const btn = "rounded-md border px-2.5 py-1 font-mono2 text-[10px] uppercase tracking-wider transition-colors disabled:opacity-40";

function AssetRow({ item, reason, onBlock, onBlockClub, onUnblock, t }) {
  const key = `${item.entity_type}-${item.entity_id}`;
  return (
    <li className="flex flex-wrap items-center gap-3 border-b border-[#1F293D]/60 py-2" data-testid={`media-asset-${key}`}>
      <MediaImage src={item.url} alt={item.label || ""} kind={item.entity_type === "player" ? "player" : "club"} size={36} surface="admin" imgClassName={item.entity_type === "player" ? "" : "object-contain p-1"} testid={`media-asset-media-${key}`} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-slate-100">{item.label} <span className="text-slate-500">#{item.entity_id}</span></p>
        <p className={`${mono} text-slate-500`}>
          {item.entity_type}{item.club_name ? ` · ${item.club_name}` : ""}
          {item.blocked && <span className="text-red-400"> · {t("admin.media_blocked")}</span>}
        </p>
      </div>
      {item.blocked ? (
        <button className={`${btn} border-[#1F293D] text-slate-300 hover:border-[#00FF87]`} onClick={() => onUnblock(item)} data-testid={`media-unblock-${key}`}>{t("admin.media_unblock")}</button>
      ) : (
        <>
          <button className={`${btn} border-red-500/60 text-red-300 hover:bg-red-500/10`} onClick={() => onBlock(item)} disabled={!reason.trim()} data-testid={`media-block-${key}`}>{t("admin.media_block")}</button>
          {item.entity_type === "team" && (
            <button className={`${btn} border-red-500/60 text-red-300 hover:bg-red-500/10`} onClick={() => onBlockClub(item.entity_id)} disabled={!reason.trim()} data-testid={`media-block-club-${key}`}>{t("admin.media_block_club")}</button>
          )}
        </>
      )}
    </li>
  );
}

export default function AdminMedia() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [reason, setReason] = useState("");
  const [results, setResults] = useState(null);
  const [list, setList] = useState([]);

  const loadList = () => { api.get("/admin/media/blocklist").then((r) => setList(r.data.items)).catch(() => navigate("/admin/login")); };
  useEffect(loadList, []); // eslint-disable-line react-hooks/exhaustive-deps

  const search = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) return;
    const r = await api.get("/admin/media/search", { params: { q } });
    setResults(r.data);
  };
  const after = (msg) => { toast.success(msg); loadList(); if (results) search(); };
  const guard = () => { if (!reason.trim()) { toast.error(t("admin.media_reason_required")); return false; } return true; };
  const block = async (item) => {
    if (!guard()) return;
    try { await api.post("/admin/media/block", { entity_type: item.entity_type, entity_id: item.entity_id, reason }); after(t("admin.media_blocked_ok")); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const blockClub = async (clubId) => {
    if (!guard()) return;
    try { const r = await api.post("/admin/media/block-club", { club_id: clubId, reason }); after(t("admin.media_club_blocked_ok", { n: r.data.players })); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const unblock = async (item) => {
    try { await api.delete(`/admin/media/block/${item.entity_type}/${item.entity_id}`); after(t("admin.media_unblocked_ok")); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const groups = results ? [["media_players", results.players], ["media_clubs", results.clubs], ["media_leagues", results.leagues]] : [];
  return (
    <div className="pt-6" data-testid="admin-media-page">
      <AdminNav />
      <h1 className="font-display text-3xl font-black uppercase tracking-tight text-slate-50">{t("admin.media_title")}</h1>
      <p className="mt-2 max-w-3xl text-sm text-slate-400">{t("admin.media_intro")}</p>

      <form onSubmit={search} className="mt-6 flex flex-wrap gap-2" data-testid="media-search-form">
        <input data-testid="media-search-input" className={`${inputCls} min-w-[260px] flex-1`} placeholder={t("admin.media_search_ph")} value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="submit" className={`${btn} border-[#00FF87] bg-[#00FF87] font-bold text-[#07090E]`} data-testid="media-search-submit">{t("admin.media_search")}</button>
        <input data-testid="media-reason-input" className={`${inputCls} w-full`} placeholder={t("admin.media_reason_ph")} value={reason} onChange={(e) => setReason(e.target.value)} />
      </form>

      {results && groups.map(([k, items]) => (
        <section key={k} className="mt-5 rounded-xl border border-[#1F293D] p-4" data-testid={`media-results-${k}`}>
          <h2 className={`${mono} mb-2 text-[#00F0FF]`}>{t(`admin.${k}`)} ({items.length})</h2>
          <ul>{items.map((it) => <AssetRow key={`${it.entity_type}-${it.entity_id}`} item={it} reason={reason} onBlock={block} onBlockClub={blockClub} onUnblock={unblock} t={t} />)}</ul>
        </section>
      ))}

      <section className="mt-8 rounded-xl border border-[#1F293D] p-4" data-testid="media-blocklist">
        <h2 className={`${mono} mb-2 text-[#00F0FF]`}>{t("admin.media_list_title")} ({list.length})</h2>
        {list.length === 0 ? <p className="text-sm text-slate-500" data-testid="media-blocklist-empty">{t("admin.media_list_empty")}</p> : (
          <ul>
            {list.map((b) => (
              <li key={`${b.entity_type}-${b.entity_id}`} className="flex flex-wrap items-center gap-3 border-b border-[#1F293D]/60 py-2" data-testid={`media-blocked-${b.entity_type}-${b.entity_id}`}>
                <span className={`${mono} w-14 text-slate-500`}>{b.entity_type}</span>
                <span className="font-mono2 text-xs text-slate-200">#{b.entity_id}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-slate-400">{b.reason}</span>
                <span className={`${mono} text-slate-600`}>{(b.blocked_at || "").slice(0, 10)} · {b.blocked_by}</span>
                <button className={`${btn} border-[#1F293D] text-slate-300 hover:border-[#00FF87]`} onClick={() => unblock(b)} data-testid={`media-unblock-${b.entity_type}-${b.entity_id}`}>{t("admin.media_unblock")}</button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
